// iter123 — Onglet "Parc" dans /facturation : récap des OR Parc terminés non facturés, par client.
// iter231 — Supervision : le patron peut corriger ligne à ligne (heures MO, quantités,
// pièces oubliées), AJOUTER/SUPPRIMER des lignes, puis « Enregistrer » sans facturer :
// il peut sortir et revenir plus tard pour facturer plusieurs OR d'un coup.
import { useEffect, useState, useMemo, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Loader2, FileText, ChevronRight, Truck, Wrench, Package, Plus, Trash2, Save, Minus,
} from "lucide-react";

function CHF(n) {
  return new Intl.NumberFormat("fr-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n || 0));
}
function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString("fr-CH", { day: "2-digit", month: "2-digit", year: "numeric" }); }
  catch { return s; }
}
const num = (v) => (v === "" || v == null ? 0 : parseFloat(String(v).replace(",", ".")) || 0);
const clean = (v) => String(v).replace(/[^0-9.,]/g, "").replace(",", ".");

/** Champ quantité avec boutons −/+ (doigts gras à l'atelier) */
function QtyInput({ value, onChange, step = 1, testid, suffix }) {
  const bump = (d) => {
    const next = Math.max(0, Math.round((num(value) + d) * 100) / 100);
    onChange(String(next));
  };
  return (
    <div className="flex items-center gap-1">
      <button type="button" onClick={() => bump(-step)} className="w-6 h-6 rounded border border-gray-300 text-gray-600 hover:bg-gray-100 flex items-center justify-center" data-testid={`${testid}-minus`}>
        <Minus className="w-3 h-3" />
      </button>
      <Input
        value={value}
        onChange={(e) => onChange(clean(e.target.value))}
        inputMode="decimal"
        className="w-16 h-7 text-right text-sm px-1"
        style={{ fontSize: 16 }}
        data-testid={testid}
      />
      <button type="button" onClick={() => bump(step)} className="w-6 h-6 rounded border border-gray-300 text-gray-600 hover:bg-gray-100 flex items-center justify-center" data-testid={`${testid}-plus`}>
        <Plus className="w-3 h-3" />
      </button>
      {suffix && <span className="text-[11px] text-gray-500 w-6">{suffix}</span>}
    </div>
  );
}

export default function FacturationParcTab() {
  const [clientsParc, setClientsParc] = useState([]);
  const [loadingClients, setLoadingClients] = useState(true);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [recap, setRecap] = useState(null);
  const [loadingRecap, setLoadingRecap] = useState(false);
  const [tarifHoraire, setTarifHoraire] = useState("");
  const [prixArticles, setPrixArticles] = useState({});  // { key: prix }
  const [prixLibres, setPrixLibres] = useState({});      // { ligne_id: prix }
  const [qtes, setQtes] = useState({});                  // iter231 { ligne_id: quantite }
  const [supprimees, setSupprimees] = useState([]);      // iter231 ids supprimés
  const [nouvelles, setNouvelles] = useState([]);        // iter231 [{tmp, or_id, type_ligne, libelle, quantite, prix}]
  const [submitting, setSubmitting] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadClients = useCallback(() => {
    setLoadingClients(true);
    api.get("/facturation-parc/clients")
      .then(({ data }) => setClientsParc(data.clients || []))
      .catch(() => setClientsParc([]))
      .finally(() => setLoadingClients(false));
  }, []);

  useEffect(() => { loadClients(); }, [loadClients]);

  const loadRecap = useCallback((cid) => {
    setLoadingRecap(true);
    api.get(`/facturation-parc/recap?client_id=${cid}`)
      .then(({ data }) => {
        setRecap(data);
        setTarifHoraire(String(data.tarif_horaire_suggere || ""));
        setPrixArticles({ ...(data.prix_articles_predefinis_suggeres || {}) });
        // iter232 — Prix des articles libres : prix de la ligne, sinon tarif
        // mémorisé pour ce client (par libellé) → plus besoin de ressaisir.
        const memoLibres = data.prix_libres_suggeres || {};
        const normLib = (s) => (s || "").trim().toLowerCase().replace(/\s+/g, " ").replace(/[.$]/g, "");
        const libres = {};
        const q = {};
        (data.or_list || []).forEach((or) => {
          [...(or.lignes_mo || []), ...(or.lignes_articles_predefinis || []), ...(or.lignes_articles_libres || [])]
            .forEach((ln) => { q[ln.id] = String(ln.quantite ?? 0); });
          (or.lignes_articles_libres || []).forEach((ln) => {
            const p = Number(ln.prix_unitaire_ht) || 0;
            libres[ln.id] = p > 0 ? p : (memoLibres[normLib(ln.libelle)] || 0);
          });
        });
        setPrixLibres(libres);
        setQtes(q);
        setSupprimees([]);
        setNouvelles([]);
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Erreur chargement"))
      .finally(() => setLoadingRecap(false));
  }, []);

  useEffect(() => { if (selectedClientId) loadRecap(selectedClientId); else setRecap(null); }, [selectedClientId, loadRecap]);

  const qte = (ln) => num(qtes[ln.id] ?? ln.quantite);
  const isDeleted = (id) => supprimees.includes(id);

  const prixDeLigne = (or, ln, kind) => {
    if (kind === "mo") return num(tarifHoraire);
    if (kind === "pre") return num(prixArticles[ln._predefined_key]);
    return num(prixLibres[ln.id]);
  };

  const orTotal = (or) => {
    let t = 0;
    (or.lignes_mo || []).forEach((ln) => { if (!isDeleted(ln.id)) t += qte(ln) * num(tarifHoraire); });
    (or.lignes_articles_predefinis || []).forEach((ln) => { if (!isDeleted(ln.id)) t += qte(ln) * num(prixArticles[ln._predefined_key]); });
    (or.lignes_articles_libres || []).forEach((ln) => { if (!isDeleted(ln.id)) t += qte(ln) * num(prixLibres[ln.id]); });
    nouvelles.filter((n) => n.or_id === or.or_id).forEach((n) => {
      const p = n.type_ligne === "main_oeuvre" ? num(tarifHoraire) : num(n.prix);
      t += num(n.quantite) * p;
    });
    return t;
  };

  const totaux = useMemo(() => {
    if (!recap) return { totalHt: 0, lignesCount: 0, orCount: 0 };
    let total = 0;
    let lignesCount = 0;
    (recap.or_list || []).forEach((or) => {
      total += orTotal(or);
      lignesCount += [...(or.lignes_mo || []), ...(or.lignes_articles_predefinis || []), ...(or.lignes_articles_libres || [])]
        .filter((ln) => !isDeleted(ln.id)).length
        + nouvelles.filter((n) => n.or_id === or.or_id).length;
    });
    return { totalHt: total, lignesCount, orCount: (recap.or_list || []).length };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recap, tarifHoraire, prixArticles, prixLibres, qtes, supprimees, nouvelles]);

  /** Payload commun ajustement / clôture */
  const buildPayload = () => {
    const orIds = (recap.or_list || []).map((or) => or.or_id);
    const prix_articles_predefinis = {};
    Object.entries(prixArticles).forEach(([k, v]) => { const p = num(v); if (p > 0) prix_articles_predefinis[k] = p; });
    const prix_lignes_libres = {};
    Object.entries(prixLibres).forEach(([k, v]) => { const p = num(v); if (p > 0) prix_lignes_libres[k] = p; });
    const lignes = [];
    (recap.or_list || []).forEach((or) => {
      [...(or.lignes_mo || []), ...(or.lignes_articles_predefinis || []), ...(or.lignes_articles_libres || [])]
        .forEach((ln) => {
          if (isDeleted(ln.id)) { lignes.push({ id: ln.id, supprimer: true }); return; }
          const q = num(qtes[ln.id] ?? ln.quantite);
          if (Math.abs(q - Number(ln.quantite || 0)) > 0.0001) lignes.push({ id: ln.id, quantite: q });
        });
    });
    const nouvelles_lignes = nouvelles
      .filter((n) => (n.libelle || "").trim() && num(n.quantite) > 0)
      .map((n) => ({
        or_id: n.or_id,
        type_ligne: n.type_ligne,
        libelle: n.libelle.trim(),
        quantite: num(n.quantite),
        unite: n.type_ligne === "main_oeuvre" ? "heure" : "piece",
        prix_unitaire_ht: n.type_ligne === "main_oeuvre" ? num(tarifHoraire) : num(n.prix),
      }));
    return {
      client_id: selectedClientId,
      or_ids: orIds,
      tarif_horaire_global: num(tarifHoraire),
      prix_articles_predefinis,
      prix_lignes_libres,
      lignes,
      nouvelles_lignes,
    };
  };

  const handleSave = async () => {
    if (!recap || !selectedClientId) return;
    setSaving(true);
    try {
      const { data } = await api.post("/facturation-parc/ajuster", buildPayload());
      toast.success(
        `Ajustements enregistrés — ${data.lignes_modifiees} corrigée(s), ${data.lignes_ajoutees} ajoutée(s), ${data.lignes_supprimees} supprimée(s)`
      );
      loadRecap(selectedClientId);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleCloturer = async () => {
    if (!recap || !selectedClientId) return;
    const tarif = num(tarifHoraire);
    if (!(tarif > 0)) { toast.error("Tarif horaire requis"); return; }
    if (!window.confirm(`Facturer ${totaux.orCount} OR pour CHF ${CHF(totaux.totalHt)} HT ?\n\nLes OR ne seront plus modifiables.`)) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/facturation-parc/cloturer", buildPayload());
      toast.success(`Facture ${data.numero} créée — ${data.n_or_inclus} OR groupés · CHF ${CHF(data.total_ttc)}`);
      setSelectedClientId("");
      setRecap(null);
      loadClients();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur clôture");
    } finally {
      setSubmitting(false);
    }
  };

  const addLigne = (orId, type_ligne) => setNouvelles((prev) => [
    ...prev,
    { tmp: `${orId}-${Date.now()}`, or_id: orId, type_ligne, libelle: "", quantite: "1", prix: "" },
  ]);

  if (loadingClients) return <div className="p-8 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#52a675]" /></div>;

  // Vue liste des clients
  if (!selectedClientId) {
    return (
      <div className="space-y-3" data-testid="facturation-parc-clients">
        <div className="text-sm text-gray-600">Clients flottes (is_parc) avec OR terminés non facturés.</div>
        {clientsParc.length === 0 ? (
          <Card className="p-10 text-center border-dashed border-2 border-gray-200">
            <Truck className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <h3 className="font-semibold text-gray-700">Rien à facturer</h3>
            <p className="text-sm text-gray-500 mt-1">Les OR Parc terminés apparaîtront ici dès que le mécano les aura saisis.</p>
          </Card>
        ) : (
          <div className="space-y-2">
            {clientsParc.map((c) => (
              <button
                key={c.client_id}
                onClick={() => setSelectedClientId(c.client_id)}
                data-testid={`fp-client-${c.client_id}`}
                className="w-full text-left p-4 bg-white border border-gray-200 rounded-xl hover:border-blue-300 transition flex items-center justify-between group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center"><Truck className="w-5 h-5" /></div>
                  <div>
                    <div className="font-medium text-gray-900">{c.client_nom}</div>
                    <div className="text-xs text-gray-500">{c.n_or} OR en attente de facturation</div>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400 group-hover:translate-x-1 transition-transform" />
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (loadingRecap || !recap) return <div className="p-8 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#52a675]" /></div>;

  const client = recap.client;
  const clientLabel = client?.raison_sociale || `${client?.prenom || ""} ${client?.nom || ""}`.trim();

  return (
    <div className="space-y-4 pb-28" data-testid="facturation-parc-recap">
      {/* Header client + actions */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <Button variant="outline" size="sm" onClick={() => setSelectedClientId("")} data-testid="fp-back">← Liste clients</Button>
          <h2 className="text-lg font-bold mt-2 flex items-center gap-2">
            <Truck className="w-5 h-5 text-blue-600" /> {clientLabel}
            <span className="text-sm font-normal text-gray-500">· {recap.or_list.length} OR · {totaux.lignesCount} lignes</span>
          </h2>
        </div>
      </div>

      {/* Inputs globaux */}
      <Card className="p-4 bg-amber-50 border-amber-200">
        <h3 className="font-semibold text-amber-900 mb-2 text-sm flex items-center gap-1 flex-wrap">
          <Wrench className="w-4 h-4" /> Tarifs appliqués globalement
          {recap.tarifs_memorises_at ? (
            <span className="text-[11px] font-normal text-amber-700" data-testid="fp-tarifs-memo">
              · mémorisés pour ce client le {fmtDate(recap.tarifs_memorises_at)} — repris sur les prochains OR
            </span>
          ) : (
            <span className="text-[11px] font-normal text-amber-700">
              · seront mémorisés pour ce client dès l&apos;enregistrement
            </span>
          )}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="text-xs font-medium text-amber-900">Tarif horaire MO (CHF) *</label>
            <Input value={tarifHoraire} onChange={(e) => setTarifHoraire(clean(e.target.value))} inputMode="decimal" data-testid="fp-tarif-horaire" className="bg-white" style={{ fontSize: 16 }} />
          </div>
        </div>
        <h4 className="font-semibold text-amber-900 mt-3 mb-2 text-xs uppercase">Prix articles pré-définis</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {Object.entries(prixArticles).map(([key, val]) => (
            <div key={key} className="flex items-center gap-1.5">
              <label className="flex-1 text-xs text-amber-900 truncate">{key.replace(/_/g, " ")}</label>
              <Input
                value={val}
                onChange={(e) => setPrixArticles({ ...prixArticles, [key]: clean(e.target.value) })}
                inputMode="decimal"
                data-testid={`fp-prix-${key}`}
                className="w-20 text-right bg-white text-sm"
                style={{ fontSize: 16 }}
              />
            </div>
          ))}
        </div>
      </Card>

      {/* Récap par véhicule, éditable (iter231) */}
      <div className="space-y-3">
        {recap.or_list.map((or) => (
          <Card key={or.or_id} className="p-4 border-gray-200" data-testid={`fp-or-${or.or_numero}`}>
            {/* En-tête OR */}
            <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-100">
              <div>
                <div className="font-mono text-sm font-semibold text-gray-900">
                  {fmtDate(or.date_termine)} · OR {or.or_numero}
                </div>
                <div className="text-xs text-gray-500">
                  {or.vehicule_label} {or.km_depose ? `· ${or.km_depose} km` : ""}
                  {or.parc_ajuste_at && <span className="text-[#2d5f3f] ml-1">· ajusté le {fmtDate(or.parc_ajuste_at)}</span>}
                </div>
              </div>
              <div className="text-right">
                <div className="font-semibold text-gray-900" data-testid={`fp-or-total-${or.or_numero}`}>CHF {CHF(orTotal(or))}</div>
                <div className="text-[10px] text-gray-400">HT</div>
              </div>
            </div>

            {/* Lignes MO — heures modifiables */}
            {(or.lignes_mo || []).map((ln) => (
              <div key={ln.id} className={`flex items-center text-sm py-1 gap-1.5 ${isDeleted(ln.id) ? "opacity-40 line-through" : ""}`} data-testid={`fp-ligne-${ln.id}`}>
                <Wrench className="w-3 h-3 text-amber-700 flex-shrink-0" />
                <div className="flex-1 truncate">{ln.libelle}</div>
                <QtyInput value={qtes[ln.id] ?? String(ln.quantite ?? 0)} onChange={(v) => setQtes({ ...qtes, [ln.id]: v })} step={0.25} suffix="h" testid={`fp-qte-${ln.id}`} />
                <span className="text-gray-400 text-xs">× {CHF(tarifHoraire)}</span>
                <div className="font-mono text-gray-900 w-24 text-right">CHF {CHF(qte(ln) * num(tarifHoraire))}</div>
                <button type="button" onClick={() => setSupprimees((p) => (p.includes(ln.id) ? p.filter((x) => x !== ln.id) : [...p, ln.id]))} className="text-red-500 hover:text-red-700" title="Supprimer la ligne" data-testid={`fp-del-${ln.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}

            {/* Lignes articles pré-définis — quantités modifiables */}
            {(or.lignes_articles_predefinis || []).map((ln) => (
              <div key={ln.id} className={`flex items-center text-sm py-1 gap-1.5 ${isDeleted(ln.id) ? "opacity-40 line-through" : ""}`} data-testid={`fp-ligne-${ln.id}`}>
                <Package className="w-3 h-3 text-blue-700 flex-shrink-0" />
                <div className="flex-1 truncate">{ln.libelle}</div>
                <QtyInput value={qtes[ln.id] ?? String(ln.quantite ?? 0)} onChange={(v) => setQtes({ ...qtes, [ln.id]: v })} step={1} suffix={ln.unite === "litre" ? "l" : "pce"} testid={`fp-qte-${ln.id}`} />
                <span className="text-gray-400 text-xs">× {CHF(prixDeLigne(or, ln, "pre"))}</span>
                <div className="font-mono text-gray-900 w-24 text-right">CHF {CHF(qte(ln) * prixDeLigne(or, ln, "pre"))}</div>
                <button type="button" onClick={() => setSupprimees((p) => (p.includes(ln.id) ? p.filter((x) => x !== ln.id) : [...p, ln.id]))} className="text-red-500 hover:text-red-700" title="Supprimer la ligne" data-testid={`fp-del-${ln.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}

            {/* Lignes libres — quantité + prix modifiables */}
            {(or.lignes_articles_libres || []).map((ln) => (
              <div key={ln.id} className={`flex items-center text-sm py-1 gap-1.5 ${isDeleted(ln.id) ? "opacity-40 line-through" : ""}`} data-testid={`fp-ligne-${ln.id}`}>
                <Package className="w-3 h-3 text-purple-700 flex-shrink-0" />
                <div className="flex-1 truncate">{ln.libelle}</div>
                <QtyInput value={qtes[ln.id] ?? String(ln.quantite ?? 0)} onChange={(v) => setQtes({ ...qtes, [ln.id]: v })} step={1} suffix={ln.unite === "litre" ? "l" : "pce"} testid={`fp-qte-${ln.id}`} />
                <Input
                  value={prixLibres[ln.id] ?? ""}
                  onChange={(e) => setPrixLibres({ ...prixLibres, [ln.id]: clean(e.target.value) })}
                  inputMode="decimal"
                  placeholder="prix"
                  data-testid={`fp-libre-${ln.id}`}
                  className="w-20 h-7 text-right text-sm"
                  style={{ fontSize: 16 }}
                />
                <div className="font-mono text-gray-900 w-24 text-right">CHF {CHF(qte(ln) * num(prixLibres[ln.id]))}</div>
                <button type="button" onClick={() => setSupprimees((p) => (p.includes(ln.id) ? p.filter((x) => x !== ln.id) : [...p, ln.id]))} className="text-red-500 hover:text-red-700" title="Supprimer la ligne" data-testid={`fp-del-${ln.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}

            {/* Nouvelles lignes ajoutées par la supervision */}
            {nouvelles.filter((n) => n.or_id === or.or_id).map((n) => (
              <div key={n.tmp} className="flex items-center text-sm py-1 gap-1.5 bg-emerald-50/60 rounded px-1" data-testid={`fp-new-${n.tmp}`}>
                {n.type_ligne === "main_oeuvre"
                  ? <Wrench className="w-3 h-3 text-amber-700 flex-shrink-0" />
                  : <Package className="w-3 h-3 text-emerald-700 flex-shrink-0" />}
                <Input
                  value={n.libelle}
                  onChange={(e) => setNouvelles((p) => p.map((x) => (x.tmp === n.tmp ? { ...x, libelle: e.target.value } : x)))}
                  placeholder={n.type_ligne === "main_oeuvre" ? "Main-d'œuvre (ex. service)" : "Pièce (ex. plaquettes avant)"}
                  className="flex-1 h-7 text-sm"
                  style={{ fontSize: 16 }}
                  data-testid={`fp-new-libelle-${n.tmp}`}
                />
                <QtyInput
                  value={n.quantite}
                  onChange={(v) => setNouvelles((p) => p.map((x) => (x.tmp === n.tmp ? { ...x, quantite: v } : x)))}
                  step={n.type_ligne === "main_oeuvre" ? 0.25 : 1}
                  suffix={n.type_ligne === "main_oeuvre" ? "h" : "pce"}
                  testid={`fp-new-qte-${n.tmp}`}
                />
                {n.type_ligne === "main_oeuvre" ? (
                  <span className="text-gray-400 text-xs">× {CHF(tarifHoraire)}</span>
                ) : (
                  <Input
                    value={n.prix}
                    onChange={(e) => setNouvelles((p) => p.map((x) => (x.tmp === n.tmp ? { ...x, prix: clean(e.target.value) } : x)))}
                    inputMode="decimal"
                    placeholder="prix"
                    className="w-20 h-7 text-right text-sm"
                    style={{ fontSize: 16 }}
                    data-testid={`fp-new-prix-${n.tmp}`}
                  />
                )}
                <div className="font-mono text-gray-900 w-24 text-right">
                  CHF {CHF(num(n.quantite) * (n.type_ligne === "main_oeuvre" ? num(tarifHoraire) : num(n.prix)))}
                </div>
                <button type="button" onClick={() => setNouvelles((p) => p.filter((x) => x.tmp !== n.tmp))} className="text-red-500 hover:text-red-700" data-testid={`fp-new-del-${n.tmp}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}

            {/* Ajouter une ligne sur CE véhicule */}
            <div className="flex gap-2 mt-2 pt-2 border-t border-dashed border-gray-200">
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => addLigne(or.or_id, "article")} data-testid={`fp-add-piece-${or.or_numero}`}>
                <Plus className="w-3 h-3 mr-1" /> Pièce oubliée
              </Button>
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => addLigne(or.or_id, "main_oeuvre")} data-testid={`fp-add-mo-${or.or_numero}`}>
                <Plus className="w-3 h-3 mr-1" /> Main-d&apos;œuvre
              </Button>
            </div>
          </Card>
        ))}
      </div>

      {/* Footer total + enregistrer / clôture */}
      <div className="sticky bottom-4 bg-white/97 backdrop-blur border border-gray-200 rounded-2xl p-4 flex items-center justify-between shadow-xl flex-wrap gap-3 z-20" data-testid="fp-action-bar">
        <div className="text-sm">
          <div className="text-gray-500">Total HT</div>
          <div className="text-xl font-bold text-gray-900" data-testid="fp-total-ht">CHF {CHF(totaux.totalHt)}</div>
          <div className="text-xs text-gray-400">+ TVA 8.1% = <b>CHF {CHF(totaux.totalHt * 1.081)}</b> TTC</div>
        </div>
        <div className="flex flex-col gap-1.5">
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleSave}
              disabled={saving || submitting}
              data-testid="fp-enregistrer"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Enregistrer
            </Button>
            <Button
              onClick={handleCloturer}
              disabled={submitting || saving || !tarifHoraire || totaux.totalHt <= 0}
              className="bg-[#52a675] hover:bg-[#3d8a5c]"
              data-testid="fp-cloturer"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
              Facturer
            </Button>
          </div>
          <div className="text-[10px] text-gray-400 text-right">
            « Enregistrer » garde les corrections sans facturer : reviens quand les prochains OR sont là.
          </div>
        </div>
      </div>
    </div>
  );
}
